import React, {useEffect} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Grid,
  TextField,
  Button
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import NumberFormat from 'react-number-format';
import { Formik } from 'formik';
import * as yup from 'yup';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { BackdropCircular, Breadcrumb } from 'components';
import {
  getStoreContactsConfigsApi,
  saveStoreNameApi,
  saveStoreContactsConfigsApi
} from 'api';
import { showNotification } from 'redux/actions/ui';
import {states} from 'helpers/usStates';


const Contacts = () => {
  const dispatch = useDispatch();

  const {
    storeContacts,
    loadingStoreContactsConfigs,
    savingStoreContactsConfigs,
    savingStoreName
  } = useSelector((state) => state.store_configs);

  useEffect(() => {
    if (!Object.keys(storeContacts).length) {
      getStoreContactsConfigsApi({dispatch});
    }
  }, []);

  const handleSaveConfigs = async (values) => {
    let isSuccessStoreContactsSave = true;
    let isSuccessStoreNameSave = true;
    const region = states.find((el) => el.value === values.region_id);

    const storeAddress = `${values.storeName}\n`
      + `${values.street_line1}\n`
      + `${values.street_line2 ? `${values.street_line2}\n` : ''}`
      + `${values.city.trim()}, ${region.name}, ${values.postcode}\n`
      + `Phone: ${values.phone}`;

    const updatedValues = Object.keys(values).filter((el) => el !== 'storeName');

    let configs = updatedValues.reduce((prevValue, currentValue) => {
      if (currentValue === 'email') {
        return (
          [...prevValue,
            {path: 'trans_email/ident_sales/email', value: values[currentValue]},
            {path: 'trans_email/ident_support/email', value: values[currentValue]},
            {path: 'trans_email/ident_general/email', value: values[currentValue]},
            {path: 'contact/email/recipient_email', value: values[currentValue]},
            {path: 'webforms/email/email', value: values[currentValue]},
            {path: 'dermpro_maxmind_config/general/email_address', value: values[currentValue]},
            {path: 'request_access/general/send_to', value: values[currentValue], scope: 'default'}
          ]
        );
      }

      return [
        ...prevValue,
        {path: `general/store_information/${currentValue}`, value: values[currentValue]},
      ];
    }, []);

    configs = [...configs, {path: 'sales/identity/address', value: storeAddress}];

    isSuccessStoreContactsSave = await saveStoreContactsConfigsApi({dispatch, configs});

    if (storeContacts.name !== values.storeName) {
      isSuccessStoreNameSave = await saveStoreNameApi({dispatch, storeName: values.storeName});
    }

    if (isSuccessStoreContactsSave && isSuccessStoreNameSave) {
      dispatch(showNotification('success', 'Configurations saved successfully.'));
    }
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          routeSegments={[
            { name: 'Settings', path: '/settings#go_live_requirements' },
            { name: 'UI Configurations', path: '/settings#UI_configurations' },
            { name: 'Store Contact Info' }
          ]}
        />
      </div>
      <div className="mt-10 mx-10">
        {!!Object.keys(storeContacts).length &&
        <Formik
          initialValues={{
            storeName: storeContacts.name,
            phone: storeContacts.phone,
            email: storeContacts.email,
            region_id: storeContacts.region_id,
            postcode: storeContacts.postcode,
            city: storeContacts.city,
            street_line1: storeContacts.street_line1,
            street_line2: storeContacts.street_line2
          }}
          validationSchema={
            yup.object({
              phone: yup
                .string()
                .matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/, 'Enter valid phone number')
                .min(14, 'Enter valid phone number')
                .max(14, 'Enter valid phone number')
                .required('Field is required'),
              email: yup
                .string()
                .email('Enter valid email')
                .required('Field is required'),
              region_id: yup
                .string()
                .required('Field is required'),
              postcode: yup
                .string()
                .matches(/^\d{5}(?:[-\s]\d{4})?$/, 'Enter valid Zip/Post Code')
                .required('Field is required'),
              city: yup
                .string()
                .required('Field is required'),
              street_line1: yup
                .string()
                .required('Field is required'),
            })
          }
          onSubmit={handleSaveConfigs}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue
          }) => (
            <>
              <form autoComplete="off" onSubmit={handleSubmit}>
                <Card className="p-8">
                  <Grid spacing={5} container>
                    <Grid item xs={12} sm={3} md={2} className="capitalize">
                      Store Name <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <TextField
                        required
                        className="w-full"
                        label="Store Name"
                        variant="outlined"
                        name="storeName"
                        value={values.storeName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.storeName && Boolean(errors.storeName)}
                        helperText={touched.storeName && errors.storeName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2} className="capitalize">
                      Phone Number <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <NumberFormat
                        required
                        customInput={TextField}
                        format="(###) ###-####"
                        mask="_"
                        className="w-full"
                        label="Phone Number"
                        variant="outlined"
                        name="phone"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2} className="capitalize">
                      Email <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <TextField
                        required
                        className="w-full"
                        label="Email"
                        variant="outlined"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2} className="capitalize">
                      Street Address <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <TextField
                        required
                        className="w-full"
                        label="Street Address"
                        variant="outlined"
                        name="street_line1"
                        value={values.street_line1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.street_line1 && Boolean(errors.street_line1)}
                        helperText={touched.street_line1 && errors.street_line1}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2} className="capitalize">
                      Street Address Line 2
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <TextField
                        className="w-full"
                        label="Street Address Line 2"
                        variant="outlined"
                        name="street_line2"
                        value={values.street_line2}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.street_line2 && Boolean(errors.street_line2)}
                        helperText={touched.street_line2 && errors.street_line2}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2} className="capitalize">
                      City <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <TextField
                        required
                        className="w-full"
                        label="City"
                        variant="outlined"
                        name="city"
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.city && Boolean(errors.city)}
                        helperText={touched.city && errors.city}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2} className="capitalize">
                      Region/State <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        name="region_id"
                        disableClearable
                        value={states.find((el) => el.value == values.region_id)}
                        options={states}
                        getOptionLabel={(option) => option.name}
                        onChange={(_, value) => {
                          setFieldValue('region_id', value.value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            required
                            {...params}
                            variant="outlined"
                            label="Region/State"
                          />
                        )}
                        renderOption={(option, { inputValue }) => {
                          const matches = match(option.name, inputValue);
                          const parts = parse(option.name, matches);
                          return (
                            <div>
                              {parts.map((part, index) => (
                                <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                                  {part.text}
                                </span>
                              ))}
                            </div>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2} className="capitalize">
                      Zip/Postal Code <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <TextField
                        required
                        className="w-full"
                        label="Zip/Postal Code"
                        variant="outlined"
                        name="postcode"
                        value={values.postcode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.postcode && Boolean(errors.postcode)}
                        helperText={touched.postcode && errors.postcode}
                      />
                    </Grid>
                  </Grid>
                </Card>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="my-10"
                  >
                    Save Configurations
                  </Button>
                </div>
              </form>
            </>
          )}
        </Formik>
        }
        <div className="flex justify-end">
          <BackdropCircular
            loading={loadingStoreContactsConfigs || savingStoreContactsConfigs || savingStoreName}
          />
        </div>
      </div>
    </div>
  );
};

export default Contacts;
